import { getAnswerByQuestionId, getHeadingContentByKey, getResolvedChecklistHeadingContent } from "./jobShiftPreStartUtils";

export const EDITABLE_PRE_START_RESPONSE_TYPES = ["radio", "text", "number"];
export const PRE_START_PHOTO_UPLOAD_PATH = "job-swms/pre-starts/submissions";

export const getQuestionFieldKey = (questionId) => `question_${questionId}`;

export const getSupervisorAnswerFieldName = (questionId) => `responses.supervisor.${getQuestionFieldKey(questionId)}.value`;
export const getSupervisorCommentFieldName = (questionId) => `responses.supervisor.${getQuestionFieldKey(questionId)}.comment`;

export const getSupervisorFlaggedResponseFieldName = (questionId) => `responses.supervisor.${getQuestionFieldKey(questionId)}.flagged_response`;

export const getWorkerAnswerFieldName = (participantIndex, questionId) => `responses.workers.${participantIndex}.${getQuestionFieldKey(questionId)}.value`;
export const getWorkerCommentFieldName = (participantIndex, questionId) => `responses.workers.${participantIndex}.${getQuestionFieldKey(questionId)}.comment`;

export const getWorkerFlaggedResponseFieldName = (participantIndex, questionId) => `responses.workers.${participantIndex}.${getQuestionFieldKey(questionId)}.flagged_response`;

export const isEditablePreStartQuestion = (question = {}) => {
  const responseType = question && question.response_config ? question.response_config.type : "";
  return EDITABLE_PRE_START_RESPONSE_TYPES.includes(responseType);
};

const isPhotoPreStartQuestion = (question = {}) => {
  const responseType = question && question.response_config ? question.response_config.type : "";
  return responseType === "photo";
};

const isFormManagedPreStartQuestion = (question = {}) => {
  return isEditablePreStartQuestion(question) || isPhotoPreStartQuestion(question);
};

const getInitialAnswerValue = (question = {}, answer = null) => {
  if (!answer || answer.value === undefined || answer.value === null) {
    return isPhotoPreStartQuestion(question) ? null : "";
  }

  if (isPhotoPreStartQuestion(question)) {
    return answer.value;
  }

  return `${answer.value}`;
};

const getInitialFlaggedResponseValue = (answer = null) => {
  if (!answer || answer.flagged_response === undefined || answer.flagged_response === null) {
    return "";
  }

  return `${answer.flagged_response}`;
};

const getInitialCommentValue = (answer = null) => {
  if (!answer || answer.comment === undefined || answer.comment === null) {
    return "";
  }

  return `${answer.comment}`;
};

const normalizeNullableText = (value) => {
  if (value === undefined || value === null) {
    return null;
  }

  const normalizedValue = `${value}`;
  return normalizedValue === "" ? null : normalizedValue;
};

const normalizeComparableAnswerValue = (question = {}, value) => {
  const normalizedValue = normalizeSubmittedAnswerValue(question, value);

  if (isPhotoPreStartQuestion(question)) {
    if (!normalizedValue) {
      return null;
    }

    if (typeof normalizedValue === "string") {
      return normalizedValue;
    }

    return normalizedValue.file_url || normalizedValue.url || normalizedValue.sign_url || normalizedValue.file_name || JSON.stringify(normalizedValue);
  }

  return normalizedValue === "" ? null : normalizedValue;
};

const getComparablePhotoValue = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value !== "object") {
    return null;
  }

  return value.file_url || value.url || value.sign_url || value.file_name || null;
};

const normalizeComparableFlaggedResponse = (question = {}, value, flaggedResponse) => {
  const responseConfig = question && question.response_config ? question.response_config : {};
  const shouldKeepFlaggedResponse = responseConfig.type === "radio"
    && responseConfig.flagOnValue != null
    && `${value || ""}` === `${responseConfig.flagOnValue}`;

  if (!shouldKeepFlaggedResponse) {
    return null;
  }

  return normalizeNullableText(flaggedResponse);
};

const getParticipantReference = (participant = {}) => {
  return participant && participant.staff_id ? `${participant.staff_id}` : null;
};

const getEditableHeadingValue = (values = {}, fieldName = "") => {
  if (!values || !fieldName || !Object.prototype.hasOwnProperty.call(values, fieldName)) {
    return undefined;
  }

  return values[fieldName];
};

export const buildChecklistResponseInitialValues = (checklist = {}) => {
  const snapshotQuestions = Array.isArray(checklist && checklist.snapshot_questions)
    ? checklist.snapshot_questions
    : [];
  const supervisorSubmission = checklist && checklist.shift_answers
    ? checklist.shift_answers.supervisor_submission || null
    : null;
  const workerSubmissions = checklist && checklist.shift_answers && Array.isArray(checklist.shift_answers.worker_submissions)
    ? checklist.shift_answers.worker_submissions
    : [];
  const initialValues = {
    prestart_checklist_heading: getResolvedChecklistHeadingContent(checklist, "prestart_checklist_heading"),
    prestart_staff_signoff: getResolvedChecklistHeadingContent(checklist, "prestart_staff_signoff"),
    responses: {
      supervisor: {},
      workers: workerSubmissions.map(() => ({}))
    }
  };

  snapshotQuestions.forEach((question) => {
    if (!isFormManagedPreStartQuestion(question)) {
      return;
    }

    const questionFieldKey = getQuestionFieldKey(question && question.id);
    const supervisorAnswer = getAnswerByQuestionId(supervisorSubmission && supervisorSubmission.answers, question && question.id);

    initialValues.responses.supervisor[questionFieldKey] = isFormManagedPreStartQuestion(question)
      ? {
        value: getInitialAnswerValue(question, supervisorAnswer),
        flagged_response: getInitialFlaggedResponseValue(supervisorAnswer),
        comment: getInitialCommentValue(supervisorAnswer)
      }
      : {
        comment: getInitialCommentValue(supervisorAnswer)
      };

    if (question && question.answer_scope === "worker") {
      workerSubmissions.forEach((participant, participantIndex) => {
        const workerAnswer = getAnswerByQuestionId(participant && participant.answers, question.id);

        initialValues.responses.workers[participantIndex][questionFieldKey] = isFormManagedPreStartQuestion(question)
          ? {
            value: getInitialAnswerValue(question, workerAnswer),
            flagged_response: getInitialFlaggedResponseValue(workerAnswer),
            comment: getInitialCommentValue(workerAnswer)
          }
          : {
            comment: getInitialCommentValue(workerAnswer)
          };
      });
    }
  });

  return initialValues;
};

export const buildPreStartQuestionEntries = (checklist = {}) => {
  const snapshotQuestions = Array.isArray(checklist && checklist.snapshot_questions)
    ? checklist.snapshot_questions
    : [];
  const supervisorSubmission = checklist && checklist.shift_answers
    ? checklist.shift_answers.supervisor_submission || null
    : null;
  const workerSubmissions = checklist && checklist.shift_answers && Array.isArray(checklist.shift_answers.worker_submissions)
    ? checklist.shift_answers.worker_submissions
    : [];

  return snapshotQuestions.map((question, index) => {
    if (question && question.answer_scope === "worker") {
      const workerAnswers = workerSubmissions.map((participant, participantIndex) => ({
        participant,
        participantIndex,
        answer: getAnswerByQuestionId(participant && participant.answers, question.id),
        fieldName: isFormManagedPreStartQuestion(question)
          ? getWorkerAnswerFieldName(participantIndex, question.id)
          : null,
        commentFieldName: getWorkerCommentFieldName(participantIndex, question.id),
        flaggedResponseFieldName: isEditablePreStartQuestion(question)
          ? getWorkerFlaggedResponseFieldName(participantIndex, question.id)
          : null
      }));

      return {
        question,
        index,
        workerAnswers,
        supervisorParticipant: supervisorSubmission,
        supervisorAnswer: getAnswerByQuestionId(supervisorSubmission && supervisorSubmission.answers, question.id),
        supervisorFieldName: isFormManagedPreStartQuestion(question)
          ? getSupervisorAnswerFieldName(question.id)
          : null,
        supervisorCommentFieldName: getSupervisorCommentFieldName(question.id),
        supervisorFlaggedResponseFieldName: isEditablePreStartQuestion(question)
          ? getSupervisorFlaggedResponseFieldName(question.id)
          : null
      };
    }

    return {
      question,
      index,
      workerAnswers: [],
      supervisorParticipant: supervisorSubmission,
      supervisorAnswer: getAnswerByQuestionId(supervisorSubmission && supervisorSubmission.answers, question.id),
      supervisorFieldName: isFormManagedPreStartQuestion(question)
        ? getSupervisorAnswerFieldName(question.id)
        : null,
      supervisorCommentFieldName: getSupervisorCommentFieldName(question.id),
      supervisorFlaggedResponseFieldName: isEditablePreStartQuestion(question)
        ? getSupervisorFlaggedResponseFieldName(question.id)
        : null
    };
  });
};

const normalizeSubmittedAnswerValue = (question = {}, value) => {
  const responseType = question && question.response_config ? question.response_config.type : "";

  if (responseType === "photo") {
    if (value === undefined || value === null || value === "") {
      return null;
    }

    if (typeof value === "string") {
      return {
        file_name: value.split('/').pop(),
        file_type: null,
        file_url: value
      };
    }

    if (typeof value === "object") {
      const resolvedFileUrl = value.file_url ? value.file_url : null;
      const resolvedFileName = value.file_name
        ? value.file_name
        : (resolvedFileUrl ? resolvedFileUrl.split('/').pop() : null);

      return {
        file_name: resolvedFileName,
        file_type: value.file_type || null,
        file_url: resolvedFileUrl
      };
    }

    return null;
  }

  if (value === undefined || value === null) {
    return "";
  }

  if (responseType === "signature" || responseType === "photo") {
    return value;
  }

  if (responseType === "number") {
    return `${value}`;
  }

  return `${value}`;
};

const upsertAnswerValue = (answers = [], question = {}, submittedValue = "", submittedFlaggedResponse = "", submittedComment = "") => {
  const questionId = question && question.id;
  const responseConfig = question && question.response_config ? question.response_config : {};
  const shouldKeepFlaggedResponse = responseConfig.type === "radio"
    && responseConfig.flagOnValue != null
    && `${submittedValue}` === `${responseConfig.flagOnValue}`;
  const existingAnswers = Array.isArray(answers) ? answers.map((answer) => ({ ...answer })) : [];
  const existingIndex = existingAnswers.findIndex((answer) => Number(answer && answer.question_id) === Number(questionId));
  const normalizedValue = normalizeSubmittedAnswerValue(question, submittedValue);
  const normalizedFlaggedResponse = shouldKeepFlaggedResponse ? `${submittedFlaggedResponse || ""}` : null;
  const normalizedComment = `${submittedComment || ""}`;

  if (existingIndex < 0 && (normalizedValue === "" || normalizedValue === null) && normalizedFlaggedResponse === null && normalizedComment === "") {
    return existingAnswers;
  }

  const nextAnswer = existingIndex >= 0
    ? {
      ...existingAnswers[existingIndex],
      value: normalizedValue,
      flagged_response: normalizedFlaggedResponse,
      comment: normalizedComment
    }
    : {
      question_id: questionId,
      value: normalizedValue,
      flagged_response: normalizedFlaggedResponse,
      comment: normalizedComment
    };

  if (existingIndex >= 0) {
    existingAnswers[existingIndex] = nextAnswer;
    return existingAnswers;
  }

  return [...existingAnswers, nextAnswer];
};

export const serializePreStartChecklistResponses = (values = {}, checklist = {}) => {
  const snapshotQuestions = Array.isArray(checklist && checklist.snapshot_questions)
    ? checklist.snapshot_questions
    : [];
  const currentChecklistHeading = getResolvedChecklistHeadingContent(checklist, "prestart_checklist_heading");
  const currentSignoffHeading = getResolvedChecklistHeadingContent(checklist, "prestart_staff_signoff");
  const supervisorSubmission = checklist && checklist.shift_answers
    ? checklist.shift_answers.supervisor_submission || {}
    : {};
  const workerSubmissions = checklist && checklist.shift_answers && Array.isArray(checklist.shift_answers.worker_submissions)
    ? checklist.shift_answers.worker_submissions
    : [];
  const formResponses = values && values.responses ? values.responses : {};
  const supervisorValues = formResponses && formResponses.supervisor ? formResponses.supervisor : {};
  const workerValues = formResponses && Array.isArray(formResponses.workers) ? formResponses.workers : [];

  const updatedSupervisorSubmission = {
    ...supervisorSubmission,
    answers: Array.isArray(supervisorSubmission && supervisorSubmission.answers)
      ? supervisorSubmission.answers.map((answer) => ({ ...answer }))
      : []
  };

  const updatedWorkerSubmissions = workerSubmissions.map((participant) => ({
    ...participant,
    answers: Array.isArray(participant && participant.answers)
      ? participant.answers.map((answer) => ({ ...answer }))
      : []
  }));

  snapshotQuestions.forEach((question) => {
    const questionFieldKey = getQuestionFieldKey(question && question.id);
    const isEditable = isEditablePreStartQuestion(question);
    const isFormManaged = isFormManagedPreStartQuestion(question);
    const currentSupervisorAnswer = getAnswerByQuestionId(updatedSupervisorSubmission.answers, question && question.id);
    const supervisorValue = supervisorValues
      && supervisorValues[questionFieldKey]
      && supervisorValues[questionFieldKey].value !== undefined
        ? supervisorValues[questionFieldKey].value
        : (currentSupervisorAnswer && currentSupervisorAnswer.value !== undefined ? currentSupervisorAnswer.value : "");
    const supervisorFlaggedResponse = supervisorValues
      && supervisorValues[questionFieldKey]
      && supervisorValues[questionFieldKey].flagged_response !== undefined
        ? supervisorValues[questionFieldKey].flagged_response
        : (currentSupervisorAnswer && currentSupervisorAnswer.flagged_response !== undefined ? currentSupervisorAnswer.flagged_response : "");
    const supervisorComment = supervisorValues
      && supervisorValues[questionFieldKey]
      && supervisorValues[questionFieldKey].comment !== undefined
        ? supervisorValues[questionFieldKey].comment
        : (currentSupervisorAnswer && currentSupervisorAnswer.comment !== undefined ? currentSupervisorAnswer.comment : "");

    updatedSupervisorSubmission.answers = upsertAnswerValue(
      updatedSupervisorSubmission.answers,
      question,
      isFormManaged ? supervisorValue : (currentSupervisorAnswer && currentSupervisorAnswer.value !== undefined ? currentSupervisorAnswer.value : ""),
      isEditable ? supervisorFlaggedResponse : (currentSupervisorAnswer && currentSupervisorAnswer.flagged_response !== undefined ? currentSupervisorAnswer.flagged_response : ""),
      supervisorComment
    );

    if (question && question.answer_scope === "worker") {
      updatedWorkerSubmissions.forEach((participant, participantIndex) => {
        const currentWorkerAnswer = getAnswerByQuestionId(participant && participant.answers, question && question.id);
        const workerQuestionValue = workerValues[participantIndex]
          && workerValues[participantIndex][questionFieldKey]
          && workerValues[participantIndex][questionFieldKey].value !== undefined
            ? workerValues[participantIndex][questionFieldKey].value
            : (currentWorkerAnswer && currentWorkerAnswer.value !== undefined ? currentWorkerAnswer.value : "");
        const workerFlaggedResponse = workerValues[participantIndex]
          && workerValues[participantIndex][questionFieldKey]
          && workerValues[participantIndex][questionFieldKey].flagged_response !== undefined
            ? workerValues[participantIndex][questionFieldKey].flagged_response
            : (currentWorkerAnswer && currentWorkerAnswer.flagged_response !== undefined ? currentWorkerAnswer.flagged_response : "");
        const workerComment = workerValues[participantIndex]
          && workerValues[participantIndex][questionFieldKey]
          && workerValues[participantIndex][questionFieldKey].comment !== undefined
            ? workerValues[participantIndex][questionFieldKey].comment
            : (currentWorkerAnswer && currentWorkerAnswer.comment !== undefined ? currentWorkerAnswer.comment : "");

        participant.answers = upsertAnswerValue(
          participant.answers,
          question,
          isFormManaged ? workerQuestionValue : (currentWorkerAnswer && currentWorkerAnswer.value !== undefined ? currentWorkerAnswer.value : ""),
          isEditable ? workerFlaggedResponse : (currentWorkerAnswer && currentWorkerAnswer.flagged_response !== undefined ? currentWorkerAnswer.flagged_response : ""),
          workerComment
        );
      });
    }
  });

  return {
    ...checklist,
    prestart_checklist_heading: getEditableHeadingValue(values, "prestart_checklist_heading") !== undefined
      ? (getEditableHeadingValue(values, "prestart_checklist_heading") || "")
      : currentChecklistHeading,
    prestart_staff_signoff: getEditableHeadingValue(values, "prestart_staff_signoff") !== undefined
      ? (getEditableHeadingValue(values, "prestart_staff_signoff") || "")
      : currentSignoffHeading,
    meta: {
      ...(checklist && checklist.meta ? checklist.meta : {}),
      heading_contents: (() => {
        const existingHeadingContents = checklist && checklist.meta && Array.isArray(checklist.meta.heading_contents)
          ? checklist.meta.heading_contents.map((item = {}) => ({ ...item }))
          : [];
        const requestedChecklistHeading = getEditableHeadingValue(values, "prestart_checklist_heading");
        const requestedSignoffHeading = getEditableHeadingValue(values, "prestart_staff_signoff");

        const upsertHeading = (headingKey, headingContent) => {
          if (headingContent === undefined) {
            return;
          }

          const existingIndex = existingHeadingContents.findIndex((item = {}) => item && item.heading_key === headingKey);
          if (existingIndex >= 0) {
            existingHeadingContents[existingIndex] = {
              ...existingHeadingContents[existingIndex],
              heading_content: headingContent || ""
            };
            return;
          }

          existingHeadingContents.push({
            heading_key: headingKey,
            heading_content: headingContent || ""
          });
        };

        upsertHeading("prestart_checklist_heading", requestedChecklistHeading);
        upsertHeading("prestart_staff_signoff", requestedSignoffHeading);

        return existingHeadingContents;
      })()
    },
    shift_answers: {
      ...(checklist && checklist.shift_answers ? checklist.shift_answers : {}),
      supervisor_submission: updatedSupervisorSubmission,
      worker_submissions: updatedWorkerSubmissions
    }
  };
};

export const buildPreStartChecklistSavePayload = (values = {}, checklist = {}) => {
  const snapshotQuestions = Array.isArray(checklist && checklist.snapshot_questions)
    ? checklist.snapshot_questions
    : [];
  const supervisorSubmission = checklist && checklist.shift_answers
    ? checklist.shift_answers.supervisor_submission || {}
    : {};
  const workerSubmissions = checklist && checklist.shift_answers && Array.isArray(checklist.shift_answers.worker_submissions)
    ? checklist.shift_answers.worker_submissions
    : [];
  const formResponses = values && values.responses ? values.responses : {};
  const supervisorValues = formResponses && formResponses.supervisor ? formResponses.supervisor : {};
  const workerValues = formResponses && Array.isArray(formResponses.workers) ? formResponses.workers : [];
  const payload = {
    answer_updates: []
  };

  const currentChecklistHeading = getResolvedChecklistHeadingContent(checklist, "prestart_checklist_heading");
  const currentStaffSignoffHeading = getResolvedChecklistHeadingContent(checklist, "prestart_staff_signoff");
  const requestedChecklistHeading = getEditableHeadingValue(values, "prestart_checklist_heading");
  const requestedStaffSignoffHeading = getEditableHeadingValue(values, "prestart_staff_signoff");

  if (requestedChecklistHeading !== undefined && `${requestedChecklistHeading || ""}` !== `${currentChecklistHeading || ""}`) {
    payload.prestart_checklist_heading = requestedChecklistHeading || null;
  }

  if (requestedStaffSignoffHeading !== undefined && `${requestedStaffSignoffHeading || ""}` !== `${currentStaffSignoffHeading || ""}`) {
    payload.prestart_staff_signoff = requestedStaffSignoffHeading || null;
  }

  snapshotQuestions.forEach((question = {}) => {
    if (!isFormManagedPreStartQuestion(question)) {
      return;
    }

    const questionFieldKey = getQuestionFieldKey(question && question.id);
    const currentSupervisorAnswer = getAnswerByQuestionId(supervisorSubmission && supervisorSubmission.answers, question && question.id);
    const submittedSupervisorValue = supervisorValues
      && supervisorValues[questionFieldKey]
      && Object.prototype.hasOwnProperty.call(supervisorValues[questionFieldKey], "value")
        ? supervisorValues[questionFieldKey].value
        : (currentSupervisorAnswer && currentSupervisorAnswer.value !== undefined ? currentSupervisorAnswer.value : "");
    const submittedSupervisorFlaggedResponse = supervisorValues
      && supervisorValues[questionFieldKey]
      && Object.prototype.hasOwnProperty.call(supervisorValues[questionFieldKey], "flagged_response")
        ? supervisorValues[questionFieldKey].flagged_response
        : (currentSupervisorAnswer && currentSupervisorAnswer.flagged_response !== undefined ? currentSupervisorAnswer.flagged_response : null);
    const submittedSupervisorComment = supervisorValues
      && supervisorValues[questionFieldKey]
      && Object.prototype.hasOwnProperty.call(supervisorValues[questionFieldKey], "comment")
        ? supervisorValues[questionFieldKey].comment
        : (currentSupervisorAnswer && currentSupervisorAnswer.comment !== undefined ? currentSupervisorAnswer.comment : null);
    const normalizedSubmittedSupervisorValue = normalizeSubmittedAnswerValue(question, submittedSupervisorValue);

    const currentSupervisorValue = normalizeComparableAnswerValue(question, currentSupervisorAnswer && currentSupervisorAnswer.value);
    const nextSupervisorValue = normalizeComparableAnswerValue(question, submittedSupervisorValue);
    const currentSupervisorFlaggedResponse = normalizeComparableFlaggedResponse(question, currentSupervisorAnswer && currentSupervisorAnswer.value, currentSupervisorAnswer && currentSupervisorAnswer.flagged_response);
    const nextSupervisorFlaggedResponse = normalizeComparableFlaggedResponse(question, submittedSupervisorValue, submittedSupervisorFlaggedResponse);
    const currentSupervisorComment = normalizeNullableText(currentSupervisorAnswer && currentSupervisorAnswer.comment);
    const nextSupervisorComment = normalizeNullableText(submittedSupervisorComment);

    if (
      currentSupervisorValue !== nextSupervisorValue
      || currentSupervisorFlaggedResponse !== nextSupervisorFlaggedResponse
      || currentSupervisorComment !== nextSupervisorComment
    ) {
      payload.answer_updates.push({
        participant_type: "supervisor",
        participant_reference: getParticipantReference(supervisorSubmission),
        question_id: question.id,
        value: isPhotoPreStartQuestion(question) ? normalizedSubmittedSupervisorValue : nextSupervisorValue,
        flagged_response: nextSupervisorFlaggedResponse,
        comment: nextSupervisorComment
      });
    }

    if (question && question.answer_scope === "worker") {
      workerSubmissions.forEach((participant = {}, participantIndex) => {
        const currentWorkerAnswer = getAnswerByQuestionId(participant && participant.answers, question && question.id);
        const workerFieldValues = workerValues[participantIndex] && workerValues[participantIndex][questionFieldKey]
          ? workerValues[participantIndex][questionFieldKey]
          : {};
        const submittedWorkerValue = Object.prototype.hasOwnProperty.call(workerFieldValues, "value")
          ? workerFieldValues.value
          : (currentWorkerAnswer && currentWorkerAnswer.value !== undefined ? currentWorkerAnswer.value : "");
        const submittedWorkerFlaggedResponse = Object.prototype.hasOwnProperty.call(workerFieldValues, "flagged_response")
          ? workerFieldValues.flagged_response
          : (currentWorkerAnswer && currentWorkerAnswer.flagged_response !== undefined ? currentWorkerAnswer.flagged_response : null);
        const submittedWorkerComment = Object.prototype.hasOwnProperty.call(workerFieldValues, "comment")
          ? workerFieldValues.comment
          : (currentWorkerAnswer && currentWorkerAnswer.comment !== undefined ? currentWorkerAnswer.comment : null);
        const normalizedSubmittedWorkerValue = normalizeSubmittedAnswerValue(question, submittedWorkerValue);
        const currentWorkerValue = normalizeComparableAnswerValue(question, currentWorkerAnswer && currentWorkerAnswer.value);
        const nextWorkerValue = normalizeComparableAnswerValue(question, submittedWorkerValue);
        const currentWorkerFlaggedResponse = normalizeComparableFlaggedResponse(question, currentWorkerAnswer && currentWorkerAnswer.value, currentWorkerAnswer && currentWorkerAnswer.flagged_response);
        const nextWorkerFlaggedResponse = normalizeComparableFlaggedResponse(question, submittedWorkerValue, submittedWorkerFlaggedResponse);
        const currentWorkerComment = normalizeNullableText(currentWorkerAnswer && currentWorkerAnswer.comment);
        const nextWorkerComment = normalizeNullableText(submittedWorkerComment);

        if (
          currentWorkerValue !== nextWorkerValue
          || currentWorkerFlaggedResponse !== nextWorkerFlaggedResponse
          || currentWorkerComment !== nextWorkerComment
        ) {
          payload.answer_updates.push({
            participant_type: "worker",
            participant_reference: getParticipantReference(participant),
            question_id: question.id,
            value: isPhotoPreStartQuestion(question) ? normalizedSubmittedWorkerValue : nextWorkerValue,
            flagged_response: nextWorkerFlaggedResponse,
            comment: nextWorkerComment
          });
        }
      });
    }
  });

  return payload;
};

export const hasPreStartChecklistSaveChanges = (payload = {}) => {
  return Boolean(
    (Array.isArray(payload.answer_updates) && payload.answer_updates.length > 0)
    || Object.prototype.hasOwnProperty.call(payload, "prestart_checklist_heading")
    || Object.prototype.hasOwnProperty.call(payload, "prestart_staff_signoff")
  );
};

const hasPendingOrFailedUploadValue = (value) => {
  if (!value || typeof value !== "object") {
    return false;
  }

  if (value.isFailed) {
    return true;
  }

  return Boolean(value.originFileObj && !value.isUploaded);
};

export const hasBlockingPreStartUploads = (values = {}, checklist = {}) => {
  const snapshotQuestions = Array.isArray(checklist && checklist.snapshot_questions)
    ? checklist.snapshot_questions
    : [];
  const formResponses = values && values.responses ? values.responses : {};
  const supervisorValues = formResponses && formResponses.supervisor ? formResponses.supervisor : {};
  const workerValues = formResponses && Array.isArray(formResponses.workers) ? formResponses.workers : [];

  return snapshotQuestions.some((question = {}) => {
    if (!isPhotoPreStartQuestion(question)) {
      return false;
    }

    const questionFieldKey = getQuestionFieldKey(question && question.id);
    const supervisorValue = supervisorValues
      && supervisorValues[questionFieldKey]
      && Object.prototype.hasOwnProperty.call(supervisorValues[questionFieldKey], "value")
        ? supervisorValues[questionFieldKey].value
        : null;

    if (hasPendingOrFailedUploadValue(supervisorValue)) {
      return true;
    }

    if (question && question.answer_scope === "worker") {
      return workerValues.some((workerValue = {}) => {
        const nextValue = workerValue
          && workerValue[questionFieldKey]
          && Object.prototype.hasOwnProperty.call(workerValue[questionFieldKey], "value")
            ? workerValue[questionFieldKey].value
            : null;

        return hasPendingOrFailedUploadValue(nextValue);
      });
    }

    return false;
  });
};